import { Routes } from '@angular/router';
import { AppLayout } from '@/layout/components/app.layout';
import { MsalGuard } from '@azure/msal-angular';
import { IDEAL_CLIENT_ROUTES } from '@/modules/ideal-client/ideal-client.routes';
import { ModuleHomeComponent } from '@/modules/home/components/module-home/module-home.component';
import { TestingLayoutComponent } from '@/layout/testing/testing-layout/testing-layout.component';
import { calidadAccessGuard } from '@/core/guards/calidad-access.guard';
import { talentoHumanoAccessGuard } from '@/core/guards/talento-humano-access.guard';
import { appAccessGuard } from '@/core/guards/app-access.guard';

export const appRoutes: Routes = [
    {
        path: '',
        component: ModuleHomeComponent,
        canActivate: [MsalGuard, appAccessGuard],
    },
    {
        path: 'tender',
        component: AppLayout,
        children: [
            {
                path: '',
                redirectTo: 'home',
                pathMatch: 'full'
            },
            {
                path: 'bidding',
                data: { breadcrumb: 'Licitación' },
                loadChildren: () => import('@/modules/bidding/bidding.routes')
            },
            {
                path: 'concreto/requisitions/:tenderId',
                data: { breadcrumb: 'Pedidos Concreto' },
                loadComponent: () => import('@/modules/bidding/components/concreto-requisition-manager/concreto-requisition-manager.component').then(c => c.ConcretoRequisitionManagerComponent)
            },
            {
                path: 'project',
                data: { breadcrumb: 'Proyectos' },
                loadChildren: () => import('@/modules/project/project.routes')
            },
            {
                path: 'apu-standard',
                data: { breadcrumb: 'APU Estándar' },
                loadChildren: () => import('@/modules/apu-standard/apu-standard.routes')
            },
            {
                path: 'home',
                data: { breadcrumb: 'Home' },
                loadChildren: () => import('@/modules/home/home.routes').then((c) => c.default)
            },
            // Concreto API Products
            {
                path: 'products',
                data: { breadcrumb: 'Productos' },
                loadChildren: () => import('@/modules/products/products.routes').then(m => m.default)
            },
            // Cliente ideal
            {
                path: 'ideal-client',
                children: IDEAL_CLIENT_ROUTES
            },
            {
                path: 'administration',
                data: { breadcrumb: 'Administración' },
                loadChildren: () => import('@/modules/administration/administration.routes')
            }
        ],
        canActivate: [MsalGuard, appAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    {
        path: 'app',
        component: AppLayout,
        children: [
            {
                path: '',
                redirectTo: 'home',
                pathMatch: 'full'
            },
            {
                path: 'settings',
                data: { breadcrumb: 'Configuración' },
                loadChildren: () => import('@/modules/settings/settings.routes').then(m => m.default)
            }
        ],
        canActivate: [MsalGuard, appAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
        canLoad: [MsalGuard],
    },
    {
        path: 'administration',
        component: AppLayout,
        children: [
            {
                path: 'catalogs',
                data: { breadcrumb: 'Catálogos' },
                loadChildren: () => import('@/modules/administration/administration.routes')
            },
        ],
        canActivate: [MsalGuard, appAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    {
        path: 'project-management',
        component: AppLayout,
        children: [
            {
                path: '',
                redirectTo: 'home',
                pathMatch: 'full'
            },
            {
                path: 'project',
                data: { breadcrumb: 'Proyectos' },
                loadChildren: () => import('@/modules/project/project.routes')
            },
            {
                path: 'handle-project',
                data: { breadcrumb: 'Proyectos' },
                loadChildren: () => import('@/modules/project/project.routes')
            }
        ],
        canActivate: [MsalGuard, appAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    {
        path: 'testing',
        component: AppLayout,
        children: [
            {
                path: '',
                data: { breadcrumb: 'Centro de Pruebas' },
                loadChildren: () => import('@/modules/testing/testing.module').then(m => m.TestingModule)
            }
        ],
        canActivate: [MsalGuard, appAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    {
        path: 'calidad',
        component: AppLayout,
        children: [
            {
                path: '',
                data: { breadcrumb: 'Gestión de Calidad' },
                loadChildren: () => import('@/modules/quality/calidad.routes')
            }
        ],
        canActivate: [MsalGuard, appAccessGuard, calidadAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    {
        path: 'human-talent',
        component: AppLayout,
        children: [
            {
                path: '',
                data: { breadcrumb: 'Talento Humano' },
                loadChildren: () => import('@/modules/talento-humano/talento-humano.routes')
            }
        ],
        canActivate: [MsalGuard, appAccessGuard, talentoHumanoAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    {
        path: 'documents',
        component: AppLayout,
        children: [
            {
                path: '',
                data: { breadcrumb: 'Control de Documentos' },
                loadChildren: () => import('@/modules/documents/documents.routes')
            }
        ],
        canActivate: [MsalGuard, appAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    {
        // Ruta global (sin guard de módulo): quien deba aprobar un paso puede no tener
        // acceso a Talento Humano ni a ningún otro módulo específico — solo necesita
        // estar autenticado y ocupar el cargo requerido por la regla.
        path: 'approvals',
        component: AppLayout,
        children: [
            {
                path: '',
                data: { breadcrumb: 'Aprobaciones' },
                loadChildren: () => import('@/modules/approvals/approvals.routes').then(m => m.default)
            }
        ],
        canActivate: [MsalGuard, appAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    {
        // Ruta de primer nivel y SIN guard de módulo, igual que `approvals` y por la misma razón:
        // cualquier empleado hace su onboarding, y un ayudante de obra o un asistente contable no
        // tienen por qué entrar al módulo de RR.HH. para ver un PDF — el guard de Talento Humano
        // no los dejaría pasar. La seguridad está en que el servidor resuelve a la persona: ninguna
        // ruta de `api/onboarding/me/*` acepta un id.
        path: 'onboarding',
        component: AppLayout,
        children: [
            {
                // Sin breadcrumb propio: lo pone cada hija. Uno acá se heredaba y dejaba
                // «Evaluaciones de período de prueba» colgando de «Onboarding».
                path: '',
                loadChildren: () => import('@/modules/onboarding/onboarding.routes')
            }
        ],
        canActivate: [MsalGuard, appAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    {
        // «Mis cursos» (E-2): de primer nivel y SIN guard de módulo, igual que `onboarding`.
        path: 'my-courses',
        component: AppLayout,
        children: [{ path: '', loadChildren: () => import('@/modules/development/my-courses.routes') }],
        canActivate: [MsalGuard, appAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    {
        // «Evaluaciones» de Desempeño para quien no entra a Talento Humano: primer nivel y sin guard de
        // módulo, igual que `onboarding`. Adentro de TH la misma pantalla es la pestaña Desempeño › Evaluaciones.
        path: 'performance',
        component: AppLayout,
        children: [
            { path: '', data: { breadcrumb: 'Desempeño' }, loadChildren: () => import('@/modules/performance/performance.routes') }
        ],
        canActivate: [MsalGuard, appAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    {
        // Centro de reportes de TODA la app (spec 2026-09-28-menu-reportes). De primer nivel y sin
        // guard de módulo, como `onboarding`: cada módulo pone su guard en `reports.routes.ts`.
        path: 'reports',
        component: AppLayout,
        children: [
            {
                path: '',
                data: { breadcrumb: 'Reportes' },
                loadChildren: () => import('@/modules/reports/reports.routes')
            }
        ],
        canActivate: [MsalGuard, appAccessGuard],
        canActivateChild: [MsalGuard, appAccessGuard],
    },
    { path: 'auth', loadChildren: () => import('@/modules/auth/auth.routes') },
    {
        // Pantalla de bloqueo para una cuenta autenticada SIN NINGÚN rol de la app (ver
        // `appAccessGuard`). Sin `MsalGuard` y fuera de `AppLayout` a propósito: es el único lugar
        // desde donde esa cuenta puede cerrar sesión o cambiarse, así que tiene que ser alcanzable
        // aunque la sesión esté a medias — con el layout puesto, el logout vive en el topbar que
        // justamente no queremos montar.
        path: 'no-access',
        loadComponent: () => import('@/modules/no-access/no-access').then((c) => c.NoAccess)
    },
    {
        path: 'notfound',
        loadComponent: () => import('@/modules/notfound/notfound').then((c) => c.Notfound)
    },
    {
        // Destino de los guards cuando el rol no alcanza. Sin guard propio: cualquier usuario
        // autenticado tiene que poder ver POR QUÉ se le negó el acceso.
        path: 'forbidden',
        loadComponent: () => import('@/modules/forbidden/forbidden').then((c) => c.Forbidden)
    },
    { path: '**', redirectTo: '/notfound' }
];
